import AsyncStorage from '@react-native-async-storage/async-storage';
import localforage from 'localforage';
import { Platform } from 'react-native';
import type { Recipe } from '../types/recipe';

/**
 * Device cache for translated recipes — one record per `recipeId × language`.
 *
 * It used to be a single JSON blob in localStorage. Measured in production that
 * blob reached 3.1 MB across 1510 entries, and localStorage is a ~5 MB budget
 * shared with redux-persist: it was the tenant that pushed heavy accounts into
 * "Storage is full" (#695, and #352 before it for the recipe index). Storing it
 * as one blob also meant a 3 MB parse on every boot and a 3 MB rewrite to save
 * one 2 KB translation.
 *
 * On web the records live in IndexedDB via localforage, which structured-clones
 * objects natively — nothing on this path stringifies or parses JSON. On native
 * they are per-key JSON in AsyncStorage (SQLite, not quota-capped).
 *
 * Everything here is regenerable: Firestore `recipeTranslations/{id}` is the
 * durable copy, never expires, and costs no AI credit to re-read. So the cache
 * is lossy on purpose — a TTL and an entry cap, trimmed by a sweep that runs in
 * idle time, never on the critical path — and it must never be able to block
 * or break a screen: an unavailable store is a miss, not an error.
 */

export interface TranslationCacheEntry {
  recipe: Recipe;
  timestamp: number;
}

/** The subset of localforage's API the cache relies on. */
export interface ObjectStoreLike {
  getItem<T>(key: string): Promise<T | null>;
  setItem<T>(key: string, value: T): Promise<unknown>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
  iterate<T>(callback: (value: T, key: string) => void): Promise<unknown>;
}

/**
 * A database of its own, not a second store inside the `paprika` database that
 * redux-persist uses for mealPlan and recipeIndex. Adding a store to an existing
 * database is a version upgrade, and any tab still holding the old version open
 * blocks it — every translation read in the new tab would then hang. A fresh
 * database has no old version to wait on, and a sweep here can never reach the
 * planner's records.
 */
export const TRANSLATION_DB_NAME = 'paprika-translations';
export const TRANSLATION_STORE_NAME = 'translations';
export const CACHE_EXPIRY_DAYS = 30;
/**
 * Production holds ~1500 translations and the corpus is bounded by catalogue ×
 * languages, so this is a safety belt against a runaway import, not a hot path.
 */
export const MAX_CACHED_TRANSLATIONS = 3000;

export const translationCacheKey = (recipeId: string, language: string): string =>
  `${recipeId}:${language}`;

const expiryMs = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

export const isExpired = (entry: { timestamp: number }, now: number): boolean =>
  now - entry.timestamp > expiryMs;

export const createNoopObjectStore = (): ObjectStoreLike => ({
  getItem: () => Promise.resolve(null),
  setItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
  clear: () => Promise.resolve(),
  iterate: () => Promise.resolve(),
});

/**
 * IndexedDB only. localforage's default driver order ends in localStorage, so
 * on a profile where IndexedDB exists but fails to open (a corrupt database, a
 * browser that disables it in private mode) it would quietly move the whole
 * cache back into the 5 MB bucket this store exists to leave — and every write
 * would look like a success. Pinning the driver turns that into a rejected
 * `ready()`, which degrades to the noop store and is reported once.
 */
export const createIndexedDbObjectStore = (
  onUnavailable?: (error: unknown) => void,
): ObjectStoreLike => {
  const instance = localforage.createInstance({
    name: TRANSLATION_DB_NAME,
    storeName: TRANSLATION_STORE_NAME,
    driver: localforage.INDEXEDDB,
  });
  let backend: ObjectStoreLike = instance;
  const readiness = instance.ready().catch((error: unknown) => {
    backend = createNoopObjectStore();
    onUnavailable?.(error);
  });
  const via = <T,>(op: (store: ObjectStoreLike) => Promise<T>): Promise<T> =>
    readiness.then(() => op(backend));
  return {
    getItem: key => via(store => store.getItem(key)),
    setItem: (key, value) => via(store => store.setItem(key, value)),
    removeItem: key => via(store => store.removeItem(key)),
    clear: () => via(store => store.clear()),
    iterate: callback => via(store => store.iterate(callback)),
  };
};

/**
 * Native has no IndexedDB. AsyncStorage stores strings, so records are JSON
 * per key under a prefix — still one small write per translation, never a blob.
 */
const NATIVE_PREFIX = 'recipe_translation:';

export const createAsyncStorageObjectStore = (): ObjectStoreLike => {
  const ownKeys = async () =>
    (await AsyncStorage.getAllKeys()).filter(k => k.startsWith(NATIVE_PREFIX));
  return {
    getItem: async <T,>(key: string) => {
      const raw = await AsyncStorage.getItem(NATIVE_PREFIX + key);
      return raw == null ? null : (JSON.parse(raw) as T);
    },
    setItem: (key, value) => AsyncStorage.setItem(NATIVE_PREFIX + key, JSON.stringify(value)),
    removeItem: key => AsyncStorage.removeItem(NATIVE_PREFIX + key),
    clear: async () => {
      const keys = await ownKeys();
      if (keys.length) await AsyncStorage.multiRemove(keys);
    },
    iterate: async <T,>(callback: (value: T, key: string) => void) => {
      const keys = await ownKeys();
      if (!keys.length) return;
      for (const [key, raw] of await AsyncStorage.multiGet(keys)) {
        if (raw != null) callback(JSON.parse(raw) as T, key.slice(NATIVE_PREFIX.length));
      }
    },
  };
};

export const createPlatformObjectStore = (
  onUnavailable?: (error: unknown) => void,
): ObjectStoreLike => {
  if (Platform.OS !== 'web') return createAsyncStorageObjectStore();
  if (typeof window === 'undefined' || !window.indexedDB) {
    onUnavailable?.(new Error('IndexedDB is not available'));
    return createNoopObjectStore();
  }
  return createIndexedDbObjectStore(onUnavailable);
};

/**
 * How long a device-cache read may take before it is treated as a miss.
 *
 * Every translation path awaits this read before it can fall through to
 * Firestore, so a read that never settles — an IndexedDB open blocked by
 * another tab, a browser bug — would leave the recipe untranslated with no
 * error. A miss costs one Firestore read; a hang costs the screen.
 */
export const CACHE_READ_DEADLINE_MS = 3_000;

export const withDeadline = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Device cache read exceeded ${ms}ms`)), ms);
    (timer as { unref?: () => void }).unref?.();
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

/**
 * Drop everything past the TTL, then everything past the entry cap, oldest
 * first. Reads every record once, which is why it belongs in idle time.
 *
 * Scan and delete are separate transactions, so a translation refreshed
 * between them would be scanned as stale and deleted as fresh. Each delete
 * re-reads the record and only removes what was actually scanned.
 *
 * Returns how many records were removed.
 */
export const sweepTranslationCache = async (
  store: ObjectStoreLike,
  now: number = Date.now(),
  maxEntries: number = MAX_CACHED_TRANSLATIONS,
): Promise<number> => {
  const unreadable: string[] = [];
  const scanned: Array<{ key: string; timestamp: number }> = [];
  await store.iterate<TranslationCacheEntry>((entry, key) => {
    if (!entry || typeof entry.timestamp !== 'number') {
      unreadable.push(key);
      return;
    }
    scanned.push({ key, timestamp: entry.timestamp });
  });

  const expired = scanned.filter(e => isExpired(e, now));
  const live = scanned.filter(e => !isExpired(e, now));
  const overflow =
    live.length > maxEntries
      ? live.sort((a, b) => a.timestamp - b.timestamp).slice(0, live.length - maxEntries)
      : [];

  let removed = 0;
  for (const key of unreadable) {
    await store.removeItem(key);
    removed += 1;
  }
  for (const { key, timestamp } of [...expired, ...overflow]) {
    const current = await store.getItem<TranslationCacheEntry>(key);
    if (current && current.timestamp !== timestamp) continue;
    await store.removeItem(key);
    removed += 1;
  }
  return removed;
};

/**
 * Run after the app has settled. `unref` so a pending sweep never keeps a node
 * process (jest) alive.
 */
export const runWhenIdle = (fn: () => void): void => {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => fn(), { timeout: 10_000 });
    return;
  }
  const timer = setTimeout(fn, 3_000);
  (timer as { unref?: () => void }).unref?.();
};

/**
 * The key the pre-#695 single-blob cache lived under. Removing it is what
 * gives the 3 MB back. It is not migrated: Firestore already holds every entry,
 * so the only cost is one Firestore read per recipe on its next open.
 */
export const LEGACY_TRANSLATION_BLOB_KEY = 'recipe_translations_v2';

export const discardLegacyTranslationBlob = async (): Promise<void> => {
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(LEGACY_TRANSLATION_BLOB_KEY);
      }
      return;
    }
    await AsyncStorage.removeItem(LEGACY_TRANSLATION_BLOB_KEY);
  } catch {
    // Nothing depends on this succeeding; the next boot tries again.
  }
};
